'use client'

import { Arrow, ArrowRight } from '@/lib/icons'
import { cn } from '@/utils'

interface PaginationProps {
    currentPage: number
    totalPages: number
    onPageChange: (page: number) => void
    className?: string
}

const Pagination = ({ currentPage, totalPages, onPageChange, className }: PaginationProps) => {
    const getVisiblePages = () => {
        if (totalPages <= 1) return [1]

        const delta = 2
        const range = []
        const rangeWithDots = []

        for (
            let i = Math.max(2, currentPage - delta);
            i <= Math.min(totalPages - 1, currentPage + delta);
            i++
        ) {
            range.push(i)
        }

        if (currentPage - delta > 2) {
            rangeWithDots.push(1, '...')
        } else {
            rangeWithDots.push(1)
        }

        rangeWithDots.push(...range)

        if (currentPage + delta < totalPages - 1) {
            rangeWithDots.push('...', totalPages)
        } else if (totalPages > 1) {
            rangeWithDots.push(totalPages)
        }

        return rangeWithDots
    }

    const visiblePages = getVisiblePages()

    return (
        <div className={cn("flex items-center justify-center gap-2 mt-8", className || "")}>
            <button
                onClick={() => {
                    const newPage = currentPage - 1
                    if (newPage >= 1) onPageChange(newPage)
                }}
                disabled={currentPage === 1}
                className={cn(
                    "inline-flex items-center gap-1 px-3 py-1.5 font-semibold text-sm rounded-lg transition-all duration-200 border-2",
                    currentPage === 1
                        ? "bg-dark3/80 text-light/20 border-light/5 cursor-not-allowed"
                        : "bg-dark3 text-light/40 border-light/15 hover:bg-dark2 hover:border-light/30 hover:brightness-110"
                )}
            >
                <Arrow className='rotate-180 text-xl' />
            </button>

            <div className="flex items-center gap-1">
                {visiblePages.map((page, index) => {
                    if (page === '...') {
                        return (
                            <span
                                key={`dots-${index}`}
                                className="px-3 py-2 text-sm font-medium text-light/40"
                            >
                                ...
                            </span>
                        )
                    }

                    return (
                        <button
                            key={page}
                            onClick={() => {
                                const targetPage = page as number
                                if (targetPage >= 1 && targetPage <= totalPages) {
                                    onPageChange(targetPage)
                                }
                            }}
                            className={cn(
                                "px-3 py-1.5 text-sm font-semibold rounded-lg transition-all duration-200 border-2",
                                currentPage === page
                                    ? "bg-accent text-dark border-accent shadow-lg"
                                    : "bg-dark3 text-light/40 border-light/15 hover:bg-dark2 hover:brightness-110"
                            )}
                        >
                            {page}
                        </button>
                    )
                })}
            </div>

            <button
                onClick={() => {
                    const newPage = currentPage + 1
                    if (newPage <= totalPages) onPageChange(newPage)
                }}
                disabled={currentPage === totalPages}
                className={cn(
                    "inline-flex items-center gap-1 px-3 py-1.5 font-semibold text-sm rounded-lg transition-all duration-200 border-2",
                    currentPage === totalPages
                        ? "bg-dark3/80 text-light/20 border-light/5 cursor-not-allowed"
                        : "bg-dark3 text-light/40 border-light/15 hover:bg-dark2 hover:border-light/30 hover:brightness-110"
                )}
            >
                <Arrow className='rotate-0 text-xl' />
            </button>
        </div>
    )
}

export default Pagination
